import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { WrenchScrewdriverIcon, ArrowPathIcon, ShieldCheckIcon, ArrowLeftOnRectangleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';

const Maintenance = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleAdminLogin = () => {
    navigate('/auth/login');
  };

  const handleLogout = async () => {
    await logout();
    window.location.reload(); // Reload to clear maintenance state
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl w-full"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
          {/* Animated Icon */}
          <motion.div
            animate={{
              rotate: [0, 10, -10, 10, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatDelay: 1,
            }}
            className="inline-block mb-6"
          >
            <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-full p-6">
              <WrenchScrewdriverIcon className="h-16 w-16 text-white" />
            </div>
          </motion.div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Under Maintenance
          </h1>

          {/* Description */}
          <p className="text-lg text-gray-600 mb-2">
            We're currently performing scheduled maintenance to improve your experience.
          </p>
          <p className="text-md text-gray-500 mb-8">
            We'll be back shortly. Thank you for your patience!
          </p>

          {/* Features being worked on */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              What we're doing
            </h3>
            <ul className="space-y-2 text-left">
              <li className="flex items-start">
                <span className="inline-block w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span className="text-gray-600">System updates and improvements</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span className="text-gray-600">Database optimization</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span className="text-gray-600">Performance enhancements</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {user ? (
              // If user is logged in, show logout button
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-pink-600 text-white font-semibold rounded-lg hover:from-red-700 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <ArrowLeftOnRectangleIcon className="h-5 w-5 mr-2" />
                Logout
              </button>
            ) : (
              // If not logged in, show admin login button
              <button
                onClick={handleAdminLogin}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-lg hover:from-emerald-700 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <ShieldCheckIcon className="h-5 w-5 mr-2" />
                Admin Login
              </button>
            )}

            <button
              onClick={handleRefresh}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <ArrowPathIcon className="h-5 w-5 mr-2" />
              Check Again
            </button>
          </div>

          {/* Admin Notice */}
          <div className="mt-6 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-sm text-emerald-800 text-center">
              <ShieldCheckIcon className="h-4 w-4 inline mr-1" />
              <strong>Administrators:</strong> You can still login to manage the platform
            </p>
          </div>

          {/* Contact Info */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              If you need immediate assistance, please contact us at{' '}
              <a
                href="mailto:support@unifylearn.com"
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                support@unifylearn.com
              </a>
            </p>
          </div>
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mt-6 text-center"
        >
          <p className="text-sm text-gray-500">
            Expected downtime: Less than 1 hour
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Maintenance;
